import { Card, CardContent } from "@/components/ui/card";
import { PenTool, Target, Music, Film, MessageCircle, DollarSign, Sparkles, TrendingUp, Briefcase, Award } from "lucide-react";

const skills = [
  {
    icon: PenTool,
    title: "Content Creation",
    description: "Create professional, engaging content that converts browsers into customers",
    stat: "Week 1",
    outcome: "Build a content portfolio",
    marketValue: "High demand skill"
  },
  {
    icon: Target,
    title: "Meta Ads Mastery",
    description: "Run data-driven Facebook & Instagram campaigns that deliver measurable ROI",
    stat: "Week 3",
    outcome: "Launch profitable campaigns",
    marketValue: "Build the Brand Reputation of a Business"
  },
  {
    icon: Music,
    title: "TikTok Advertising",
    description: "Leverage TikTok's powerful platform to reach younger demographics effectively",
    stat: "Week 4",
    outcome: "Tap into new markets",
    marketValue: "Emerging opportunity"
  },
  {
    icon: Film,
    title: "Video Production",
    description: "Professional editing with industry-standard tools (Canva & CapCut)",
    stat: "Week 2",
    outcome: "Create studio-quality content",
    marketValue: "Freelance ready"
  },
  {
    icon: MessageCircle,
    title: "Client Communication",
    description: "Master customer service strategies that build trust and drive retention",
    stat: "Ongoing",
    outcome: "Handle clients professionally",
    marketValue: "Essential business skill"
  },
  {
    icon: DollarSign,
    title: "Sales & Conversion",
    description: "Proven techniques to close deals and maximize customer lifetime value",
    stat: "Week 4",
    outcome: "Generate consistent revenue",
    marketValue: "Direct income impact"
  },
];

export function SkillsSection() {
  return (
    <section className="relative min-h-screen overflow-hidden bg-gradient-to-b from-turquoise-very-dark via-[hsl(186,80%,10%)] to-turquoise-very-dark py-12 md:py-16 lg:py-20">
      {/* Premium Background Pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-radial from-turquoise/10 via-transparent to-transparent blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-radial from-maroon/10 via-transparent to-transparent blur-3xl" />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      <div className="container relative z-10 px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-maroon/20 to-turquoise/20 rounded-full border border-maroon/30 mb-4 md:mb-6">
            <Sparkles className="w-3 h-3 text-turquoise fill-turquoise" />
            <span className="text-xs font-semibold text-white">Career-Ready Curriculum</span>
          </div>
          
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mb-4 md:mb-6 leading-tight text-white">
            Master In-Demand Skills
            <span className="block mt-1.5 md:mt-2 text-transparent bg-clip-text bg-gradient-to-r from-turquoise via-turquoise-dark to-maroon">
              That Employers Value
            </span>
          </h2>
          
          <p className="text-gray-300 text-sm md:text-base lg:text-lg leading-relaxed max-w-2xl mx-auto">
            Gain practical, job-ready expertise in high-demand digital marketing skills. Each module is designed to add tangible value to your resume and professional portfolio.
          </p>
        </div>

        {/* Skills Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 max-w-7xl mx-auto">
          {skills.map((skill, index) => {
            const Icon = skill.icon;
            
            return (
              <div
                key={skill.title}
                className="group animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <Card className="relative overflow-hidden bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] border border-white/10 hover:border-turquoise/50 transition-all duration-500 hover:-translate-y-2 h-full shadow-2xl hover:shadow-turquoise/20">
                  {/* Hover gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-turquoise/5 to-maroon/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <CardContent className="p-5 md:p-6 lg:p-7 relative">
                    {/* Week badge */}
                    <div className="absolute top-4 right-4 md:top-5 md:right-5">
                      <span className="px-2 md:px-3 py-1 bg-gradient-to-br from-white/5 to-white/10 text-white text-xs font-bold rounded-full border border-white/10">
                        {skill.stat}
                      </span>
                    </div>

                    {/* Icon with animated gradient */}
                    <div className="relative mb-4 md:mb-5 group-hover:animate-bounce-gentle">
                      <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-gradient-to-br from-maroon to-maroon-dark flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:shadow-maroon/30 transition-all duration-500">
                        <Icon className="w-6 h-6 md:w-8 md:h-8 text-white" strokeWidth={2} />
                      </div>
                      {/* Glow effect on hover */}
                      <div className="absolute inset-0 w-14 h-14 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-gradient-to-br from-maroon to-maroon-dark blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500" />
                    </div>

                    {/* Content */}
                    <h3 className="font-heading text-lg md:text-xl text-white mb-2 md:mb-3 font-bold leading-tight">
                      {skill.title}
                    </h3>
                    
                    <p className="text-gray-300 leading-relaxed text-xs md:text-sm mb-3 md:mb-4">
                      {skill.description}
                    </p>

                    {/* Outcome */}
                    <div className="flex items-center gap-1.5 mb-2">
                      <TrendingUp className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                      <span className="text-emerald-400 text-xs font-semibold">
                        {skill.outcome}
                      </span>
                    </div>

                    {/* Market Value */}
                    <div className="flex items-center gap-1.5">
                      <Briefcase className="w-3.5 h-3.5 text-turquoise flex-shrink-0" />
                      <span className="text-turquoise text-xs font-medium">
                        {skill.marketValue}
                      </span>
                    </div>

                    {/* Bottom accent line */}
                    <div className="mt-4 md:mt-6 pt-3 md:pt-4 border-t border-white/10 group-hover:border-turquoise/30 transition-colors duration-300">
                      <span className="text-xs font-semibold text-turquoise opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-1">
                        <Award className="w-3 h-3" />
                        Portfolio-ready training →
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>

        {/* Value Proposition Cards */}
        <div className="mt-12 md:mt-16 grid md:grid-cols-3 gap-4 md:gap-5 max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] border border-white/10 rounded-xl p-5 md:p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                <Award className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-white font-bold text-sm md:text-base">Certification Included</h3>
            </div>
            <p className="text-gray-400 text-xs md:text-sm leading-relaxed">
              Receive a recognized certificate upon completion to showcase your expertise to employers
            </p>
          </div>

          <div className="bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] border border-white/10 rounded-xl p-5 md:p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-white font-bold text-sm md:text-base">Job Placement Support</h3>
            </div>
            <p className="text-gray-400 text-xs md:text-sm leading-relaxed">
              Access our network of hiring partners and get career guidance from industry professionals
            </p>
          </div>

          <div className="bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] border border-white/10 rounded-xl p-5 md:p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-white font-bold text-sm md:text-base">Real Portfolio Projects</h3>
            </div>
            <p className="text-gray-400 text-xs md:text-sm leading-relaxed">
              Build a professional portfolio with actual campaign examples to impress potential employers
            </p>
          </div>
        </div>

        {/* Bottom CTA - Career Focused */}
        <div className="mt-12 md:mt-16 text-center max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] border border-turquoise/30 rounded-xl md:rounded-2xl p-6 md:p-8 lg:p-10">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-turquoise" />
              <h3 className="text-white text-lg md:text-xl lg:text-2xl font-bold">
                Complete Skill Package
              </h3>
              <Sparkles className="w-5 h-5 text-turquoise" />
            </div>
            <p className="text-white text-sm md:text-base lg:text-lg font-medium mb-2">
              All <span className="font-bold text-turquoise">6 professional skills</span> included in your training program
            </p>
            <p className="text-gray-400 text-xs md:text-sm mb-4">
              Plus: Lifetime access to materials • Industry certification • Career support • Portfolio development
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500/20 to-emerald-600/20 rounded-lg border border-emerald-500/30">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-semibold">
                Job-ready in just 4 weeks
              </span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .bg-gradient-radial {
          background: radial-gradient(circle, var(--tw-gradient-stops));
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.6s ease-out backwards;
        }

        @keyframes bounce-gentle {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-8px) rotate(3deg);
          }
        }
        .animate-bounce-gentle {
          animation: bounce-gentle 0.6s ease-in-out;
        }
      `}</style>
    </section>
  );
}